import "@/App.css";
import "@/styles/login.css";
import "@/operationalBase/demoMode/demoMode.css";
import "./lib/i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { TourProvider } from "./components/QuickTour";
import { InvestigationTourProvider } from "./components/InvestigationHelp";
import { NavLayoutProvider } from "./lib/NavLayoutContext";
import { HelperDockProvider } from "./lib/HelperDockContext";
import HelperDock from "./components/helper/HelperDock";
import { ThemeProvider } from "./lib/ThemeContext";
import { CapabilityProvider } from "./operationalBase";
import EveningModePrompt from "./components/EveningModePrompt";

// Layout
import Layout from "./components/layout/Layout";

// Pages
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import TapEntry from "./pages/TapEntry";
import VisitorThreadLink from "./pages/VisitorThreadLink";
import TicketReturn from "./pages/TicketReturn";
import Dashboard from "./pages/Dashboard";
import Cards from "./pages/Cards";
import CardFleetOpsRoute from "./pages/CardFleetOpsRoute";
import Employees from "./pages/Employees";
import Locations from "./pages/Locations";
import SurveysList from "./modules/surveys/pages/SurveysList";
import SurveyEditor from "./modules/surveys/pages/SurveyEditor";
import SurveyResponses from "./modules/surveys/pages/SurveyResponses";
import SurveyPublic from "./modules/surveys/pages/SurveyPublic";
import MultiLocationPublic from "./pages/MultiLocationPublic";
import MultiLocationLinks from "./pages/MultiLocationLinks";
import Promos from "./pages/Promos";
import PromoScreen from "./pages/PromoScreen";
import MultiLocationLinkEditor from "./pages/MultiLocationLinkEditor";
import GuidedSurveyProgress from "./modules/surveys/guided/GuidedSurveyProgress";
import NeoSalesStatus from "./pages/NeoSalesStatus";
import Analytics from "./pages/Analytics";
import AdminClients from "./pages/AdminClients";
import AdminUsers from "./pages/AdminUsers";
import AdminInsightObservation from "./pages/AdminInsightObservation";
import AdminSettings from "./pages/AdminSettings";
import MySettings from "./pages/MySettings";
import OrganizationSettings from "./pages/OrganizationSettings";
import AdminDiagnostics from "./pages/AdminDiagnostics";
import AskChefVoicesPage from "./pages/admin/AskChefVoicesPage";
import AskChefReviewPage from "./pages/admin/AskChefReviewPage";
import MaraddoDiagnostic from "./pages/MaraddoDiagnostic";
import AdminReconciliation from "./pages/AdminReconciliation";
import TapHealthMonitor from "./pages/TapHealthMonitor";
// P_WELCOME_HUB_SCHEDULER_TAB_1.0 — the standalone AdminScheduler
// page was retired in favour of a tab inside UserWelcomeDispatch.
// The /admin/scheduler URL now redirects so existing bookmarks
// continue to work.
import UserWelcomeDispatch from "./pages/UserWelcomeDispatch";
import ROIDashboard from "./pages/ROIDashboard";
import SalesReport from "./pages/SalesReport";
import SalesCalculationTool from "./pages/SalesCalculationTool";
import PlatformValuesSettings from "./pages/PlatformValuesSettings";
import EmployeeNotifications from "./pages/EmployeeNotifications";
import ShiftInsights from "./pages/ShiftInsights";
import LocationHealth from "./pages/LocationHealth";
import ExecutiveSummary from "./pages/ExecutiveSummary";
import CommandCenter from "./pages/CommandCenter";
import ManualList from "./pages/manual/ManualList";
import ManualSection from "./pages/manual/ManualSection";
import ManualPublic from "./pages/manual/ManualPublic";
import CamerasPage from "./pages/CamerasPage";
import BrandComparison from "./pages/BrandComparison";
import AdminBrands from "./pages/AdminBrands";
import AdminBroadcasts from "./pages/AdminBroadcasts";
import AdminRecognition from "./pages/AdminRecognition";
import AdminActivity from "./pages/AdminActivity";
import Timeline from "./pages/Timeline";
import LandingPage from "./pages/LandingPage";
import ReviewShield from "./pages/ReviewShield";
import GoalFan from "./pages/goalgame/GoalFan";
import GoalVenueJoin from "./pages/goalgame/GoalVenueJoin";
import LoyaltyPortal from "./pages/loyalty/LoyaltyPortal";
import LoyaltyConfirm from "./pages/loyalty/LoyaltyConfirm";
import LoyaltyDesk from "./pages/loyalty/LoyaltyDesk";
import LoyaltyStaffSetup from "./pages/loyalty/LoyaltyStaffSetup";
import HowPointsWork from "./pages/patron/HowPointsWork";
import RemotePlay from "./pages/patron/RemotePlay";
import RemoteUnsubscribe from "./pages/patron/RemoteUnsubscribe";
import GoalGameControl from "./pages/goalgame/GoalGameControl";
import GameResults from "./pages/GameResults";
import GoalTapnwinPlayers from "./pages/goalgame/GoalTapnwinPlayers";
import LiveGameView from "./pages/LiveGameView";
import PatronPinReset from "./pages/PatronPinReset";
import GoalOperator from "./pages/goalgame/GoalOperator";
import GoalTrigger from "./pages/goalgame/GoalTrigger";
import GlobalReview from "./pages/GlobalReview";
import AuditLogs from "./pages/AuditLogs";
import CommunicationPreferences from "./pages/CommunicationPreferences";
import RedemptionInvestigation from "./pages/RedemptionInvestigation";
import PermissionsManager from "./pages/PermissionsManager";
import ManagementAudit from "./pages/ManagementAudit";
import URLRegistry from "./pages/URLRegistry";
import ReviewShieldResults from "./pages/ReviewShieldResults";
import PatronCommand from "./pages/patron/PatronCommand";
import RecoveryBridge from "./pages/RecoveryBridge";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import RespondNow from "./pages/RespondNow";
import StaffMenu from "./pages/StaffMenu";
import VoucherDisplay from "./pages/VoucherDisplay";
import VoucherScan from "./pages/VoucherScan";
import VoucherRedeem from "./pages/VoucherRedeem";
import VoucherDashboard from "./pages/VoucherDashboard";
import VoucherSettings from "./pages/VoucherSettings";
import TestDataCleanup from "./pages/TestDataCleanup";
import ManagerMode from "./pages/ManagerMode";
import ManagerGameView from "./pages/ManagerGameView";
import DemoVoucher from "./pages/DemoVoucher";
import DemoManagerEntry from "./pages/DemoManagerEntry";
import DemoTenantEntry from "./pages/DemoTenantEntry";
import HotelDemoLauncher from "./pages/HotelDemoLauncher";
import ShowroomBackButton from "./components/ShowroomBackButton";
import AutoShowroomSwitcher from "./components/AutoShowroomSwitcher";
import AutoDemoSwitcher from "./components/AutoDemoSwitcher";
import MedDemoPatientReset from "./components/MedDemoPatientReset";
import HotelDemoSwitcher from "./components/HotelDemoSwitcher";
import GroceryDemoSwitcher, { DemoGroceryCustomerEntry, DemoGroceryHouseholdEntry } from "./components/grocery/GroceryDemo";
import GroceryCardPage from "./pages/grocery/GroceryCardPage";
import GroceryIconAtlasPage from "./pages/grocery/GroceryIconAtlasPage";
import GroceryPropertyAddon from "./pages/grocery/GroceryPropertyAddon";
import GroceryStorePage from "./pages/grocery/GroceryStorePage";
import SharedListPage from "./pages/grocery/SharedListPage";
// THROWAWAY: grocery Insights wireframes for owner approval. Delete with the page.
import InsightsWireframes from "./pages/InsightsWireframes";
import DemoHotelEntry from "./pages/DemoHotelEntry";
import DemoAutoBuyerEntry from "./pages/DemoAutoBuyerEntry";
import { DemoMedPatientEntry, DemoMedStaffEntry } from "./pages/DemoMedEntry";
import DemoAutoSalespersonEntry from "./pages/DemoAutoSalespersonEntry";
import DemoAutoInsuranceBrokerEntry from "./pages/DemoAutoInsuranceBrokerEntry";
import DemoAutoCommandCenterEntry from "./pages/DemoAutoCommandCenterEntry";
import DemoAutoSplitScreen from "./pages/DemoAutoSplitScreen";
import ManagerQrEntry from "./pages/ManagerQrEntry";
import ManagerAccessAdmin from "./pages/ManagerAccessAdmin";
import Welcome from "./pages/Welcome";
import WeeklyReportCard from "./pages/WeeklyReportCard";
import { WelcomeRedirectGate } from "./lib/welcomeGate";

// Operational Base — Phase 2 dark luxury shell (lives outside the legacy Layout).
import OperationalBaseHome from "./operationalBase/pages/OperationalBaseHome";
import ModuleManagement from "./operationalBase/pages/ModuleManagement";
import ActivityPulsePage from "./operationalBase/modules/activityPulse/ActivityPulse";
import TemplatesPage from "./operationalBase/pages/Templates";
import SetupWizard from "./operationalBase/pages/SetupWizard";
import OperationalBaseAnalytics from "./operationalBase/pages/Analytics";
import ReviewAssistant from "./operationalBase/pages/ReviewAssistant";
import ReviewShieldIntelligence from "./operationalBase/pages/ReviewShieldIntelligence";
import MyTapActivation from "./pages/MyTapActivation";
import { GuidanceProvider } from "./operationalBase/guidance/GuidanceProvider";
import { DemoProvider } from "./operationalBase/demo/DemoContext";
import { DemoModeProvider } from "./operationalBase/demoMode/DemoModeContext";
import { DemoModeTransition } from "./operationalBase/demoMode/DemoModeUI";
import { DemoRouteGuard } from "./operationalBase/demoMode/DemoRouteGuard";
import { InsightProvider } from "./insight/InsightContext";
import InsightOverlay from "./insight/InsightOverlay";
import { useStrictI18n } from "./lib/i18nStrict";
import { isGuestRoute } from "./lib/guestRoute";

// Hotel — August 2026 additions
import HotelGuestFlow from "./pages/HotelGuestFlow";
import HotelAssistant from "./pages/hotelAssistant/HotelAssistant";
import HotelStaffHub from "./pages/hotelAssistant/StaffHub";
import HotelResults from "./pages/HotelResults";
import HotelSetup from "./pages/HotelSetup";
import HotelRooms from "./pages/HotelRooms";
import UsedVehicles from "./pages/UsedVehicles";
import VehicleExperience from "./pages/VehicleExperience";
import FinanceInsurance from "./pages/FinanceInsurance";
import TradeInEstimator from "./pages/TradeInEstimator";
import VehicleDetailsSpecs from "./pages/VehicleDetailsSpecs";
import VehicleRouteGuard from "./components/VehicleRouteGuard";
import VehicleCompare from "./pages/VehicleCompare";
import VehicleLeads from "./pages/VehicleLeads";
import SalesDesk from "./pages/SalesDesk";
import HotelOpsPortal from "./pages/HotelOpsPortal";
import CardProgrammingGuide from "./pages/CardProgrammingGuide";
import HotelManual from "./pages/HotelManual";

// Tiny dev-time guard component. Hook runs only in non-production builds.
function StrictI18nGuard() {
    useStrictI18n();
    return null;
}

/** Operational Base is an org-level switch, DEFAULT OFF. When it is off every
 *  /operational-base route sends the user to the Dashboard instead. Super
 *  admins always keep access (/auth/me returns true for them). */
function OperationalBaseGate({ children }) {
    const { user, loading } = useAuth();
    if (loading) return children;
    if (user && user.operational_base_enabled === false) return <Navigate to="/dashboard" replace />;
    return children;
}

/** Renders portal-only UI, and nothing at all on a guest route. */
function PortalChromeOnly({ children }) {
    const { pathname } = useLocation();
    if (isGuestRoute(pathname)) return null;
    return children;
}

function App() {
    return (
        <NavLayoutProvider>
        <AuthProvider>
            <ThemeProvider>
                <CapabilityProvider>
                    <HelperDockProvider>
                    <BrowserRouter>
                        <TourProvider>
                            <InvestigationTourProvider>
                            <GuidanceProvider>
                                <DemoProvider>
                                    <DemoModeProvider>
                                        <InsightProvider>
                                        <StrictI18nGuard />
                                        <WelcomeRedirectGate />
                                        <DemoModeTransition />
                                    {/* PORTAL-ONLY CHROME. A guest who tapped a
                                        card is not a portal user: Help Mode
                                        (InsightOverlay) and the evening prompt
                                        must never render on a guest route,
                                        whatever is in this device's storage. */}
                                    <PortalChromeOnly>
                                        <EveningModePrompt />
                                        <InsightOverlay />
                                        {/* THE HELPER DOCK. Above the routes on
                                            purpose: a page change must never
                                            close it or clear the conversation. */}
                                        <HelperDock />
                                    </PortalChromeOnly>
                                    {/* DEMO CHROME is NOT portal chrome. These four
                                        are the presenter's only way back out of a
                                        demo persona, and every one of them lives on
                                        a guest-shaped route (/assistant/:card,
                                        /hotel/:card, /vehicle/:card). Wrapped in
                                        PortalChromeOnly they rendered NOWHERE, which
                                        is why the hotel demo (and the property
                                        tenant's "Back to Manager") had no way back.
                                        Each one self-hides unless the page is a DEMO
                                        card / DEMO org, so a real client's guest
                                        never sees them. */}
                                    <ShowroomBackButton />
                                    <AutoShowroomSwitcher />
                                    <AutoDemoSwitcher />
                                    <HotelDemoSwitcher />
                                    <GroceryDemoSwitcher />
                                    <MedDemoPatientReset />
                        <Routes>
                            {/* Public Routes */}
                            <Route path="/login" element={<Login />} />
                            <Route path="/change-password" element={<ChangePassword />} />
                            <Route path="/tap-entry/:locationId" element={<TapEntry />} />
                            <Route path="/visitor-thread/:linkToken" element={<VisitorThreadLink />} />
                            <Route path="/ticket/:linkToken" element={<TicketReturn />} />
                            <Route path="/landing/:cardId" element={<LandingPage />} />
                            <Route path="/review-shield/:cardId" element={<ReviewShield />} />
                            <Route path="/review-shield-experience/:experienceId" element={<ReviewShield />} />
                            <Route path="/global-review/:cardId" element={<GlobalReview />} />
                            {/* Goal Game — Phase 0 demo. Operator link + Fan table card. */}
                            <Route path="/goal/operator/:token" element={<GoalOperator />} />
                            {/* A venue's own public join link, posted on social media. */}
                            <Route path="/goal/join/:token" element={<GoalVenueJoin />} />
                            <Route path="/goal/:cardId" element={<GoalFan />} />
                            <Route path="/patron/:cardId/how-points-work" element={<HowPointsWork />} />
                            <Route path="/patron/pin-reset" element={<PatronPinReset />} />
                            <Route path="/play/unsub/:token" element={<RemoteUnsubscribe />} />
                            <Route path="/play/:token" element={<RemotePlay />} />
                            <Route path="/goal-trigger/:token" element={<GoalTrigger />} />
                            <Route path="/hotel/:cardId" element={<HotelGuestFlow />} />
                            {/* Grocery — the fridge card and its showroom personas */}
                            <Route path="/grocery/icon-atlas" element={<GroceryIconAtlasPage />} />
                            <Route path="/grocery/:cardId" element={<GroceryCardPage />} />
                            <Route path="/loyalty/confirm" element={<LoyaltyConfirm />} />
                            <Route path="/loyalty/staff-setup" element={<LoyaltyStaffSetup />} />
                            <Route path="/loyalty-desk/:cardId" element={<LoyaltyDesk />} />
                            <Route path="/loyalty/:cardId" element={<LoyaltyPortal />} />
                            <Route path="/l/:token" element={<SharedListPage />} />
                            <Route path="/demo/grocery/customer" element={<DemoGroceryCustomerEntry />} />
                            <Route path="/demo/grocery/household" element={<DemoGroceryHouseholdEntry />} />
                            <Route path="/demo/grocery/store" element={<GroceryStorePage />} />
                            <Route path="/assistant/staff/:cardId" element={<HotelStaffHub />} />
                            <Route path="/assistant/:cardId" element={<HotelAssistant />} />
                            <Route path="/bridge/:token" element={<RecoveryBridge />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/mytap/activate" element={<MyTapActivation />} />
                            <Route path="/respond-now/:feedbackId" element={<RespondNow />} />
                            <Route path="/staff/:cardId" element={<StaffMenu />} />
                            <Route path="/manager/qr/:token" element={<ManagerQrEntry />} />
                            <Route path="/manager/:cardId/game-results" element={<ManagerGameView view="results" />} />
                            <Route path="/manager/:cardId/live-game-view" element={<ManagerGameView view="live" />} />
                            <Route path="/manager/:cardId" element={<ManagerMode />} />
                            <Route path="/demo/manager" element={<DemoManagerEntry />} />
                            <Route path="/demo/tenant" element={<DemoTenantEntry />} />
                            <Route path="/demo/automotive/buyer" element={<DemoAutoBuyerEntry />} />
                            <Route path="/demo/automotive/salesperson" element={<DemoAutoSalespersonEntry />} />
                            <Route path="/demo/automotive/insurance-broker" element={<DemoAutoInsuranceBrokerEntry />} />
                            <Route path="/demo/automotive/command-center" element={<DemoAutoCommandCenterEntry />} />
                            <Route path="/demo/automotive/split" element={<DemoAutoSplitScreen />} />
                            {/* Medical — three no-login personas, one per tab */}
                            <Route path="/demo/medical/patient" element={<DemoMedPatientEntry />} />
                            <Route path="/demo/medical/coordinator" element={<DemoMedStaffEntry persona="coordinator" />} />
                            <Route path="/demo/medical/head-office" element={<DemoMedStaffEntry persona="head-office" />} />
                            <Route path="/demo/hotel" element={<HotelDemoLauncher />} />
                            <Route path="/demo/hotel/guest" element={<DemoHotelEntry role="guest" />} />
                            <Route path="/demo/hotel/concierge" element={<DemoHotelEntry role="concierge" />} />
                            <Route path="/demo/hotel/valet" element={<DemoHotelEntry role="valet" />} />
                            <Route path="/demo/hotel/security" element={<DemoHotelEntry role="security" />} />
                            <Route path="/demo/hotel/housekeeping" element={<DemoHotelEntry role="housekeeping" />} />
                            <Route path="/demo/hotel/manager" element={<DemoHotelEntry role="manager" />} />
                            <Route path="/demo/:cardId" element={<DemoVoucher />} />
                            <Route path="/vehicle/:cardId/finance" element={<FinanceInsurance />} />
                            <Route path="/vehicle/:cardId/trade-in" element={<TradeInEstimator />} />
                            <Route path="/vehicle/:cardId/details" element={<VehicleDetailsSpecs />} />
                            <Route path="/vehicle/compare" element={<VehicleCompare />} />
                            <Route path="/vehicle/:cardId" element={<VehicleRouteGuard />} />
                            <Route path="/voucher/scan/:token" element={<VoucherScan />} />
                            <Route path="/voucher/:code" element={<VoucherDisplay />} />
                            <Route path="/redeem" element={<VoucherRedeem />} />
                            <Route path="/s/:slug" element={<SurveyPublic />} />
                            <Route path="/m/:slug" element={<MultiLocationPublic />} />
                            {/* The manual, one section, no login. */}
                            <Route path="/manual/view/:token" element={<ManualPublic />} />
                            {/* Public promo screen — shown instead of the card's normal flow. */}
                            <Route path="/promo/:cardId" element={<PromoScreen />} />
                        
                        {/* Protected Routes */}
                        <Route path="/welcome" element={<Welcome />} />
                        <Route path="/dashboard" element={
                            <Layout>
                                <Dashboard />
                            </Layout>
                        } />
                        <Route path="/surveys" element={
                            <Layout>
                                <SurveysList />
                            </Layout>
                        } />
                        {/* P_GUIDED_SURVEY_EXPERIENCES_1.0 — progress UX */}
                        <Route path="/surveys/guided/creating" element={
                            <Layout>
                                <GuidedSurveyProgress />
                            </Layout>
                        } />
                        <Route path="/surveys/:id" element={
                            <Layout>
                                <SurveyEditor />
                            </Layout>
                        } />
                        <Route path="/surveys/:id/responses" element={
                            <Layout>
                                <SurveyResponses />
                            </Layout>
                        } />
                        <Route path="/cards" element={
                            <Layout>
                                <Cards />
                            </Layout>
                        } />
                        {/* Goal Game — one super-admin control page. /goal-admin is kept
                            as a redirect so older bookmarks still land somewhere useful. */}
                        <Route path="/goal-game" element={
                            <Layout>
                                <GoalGameControl />
                            </Layout>
                        } />
                        <Route path="/goal-admin" element={<Navigate to="/goal-game" replace />} />
                        {/* Game Results — one page for every game's plays. */}
                        {/* Live Game View — tonight, right now. Its own tab. */}
                        <Route path="/live-game-view" element={
                            <Layout>
                                <LiveGameView />
                            </Layout>
                        } />
                        <Route path="/game-results" element={
                            <Layout>
                                <GameResults />
                            </Layout>
                        } />
                        {/* THE PLAYERS LIST — one tap from the Tap'N Win page. */}
                        <Route path="/tapnwin-players" element={
                            <Layout>
                                <GoalTapnwinPlayers />
                            </Layout>
                        } />
                        <Route path="/used-vehicles" element={
                            <Layout>
                                <UsedVehicles />
                            </Layout>
                        } />
                        <Route path="/vehicle-leads" element={
                            <Layout>
                                <VehicleLeads />
                            </Layout>
                        } />
                        <Route path="/sales-desk" element={
                            <Layout>
                                <SalesDesk />
                            </Layout>
                        } />
                        <Route path="/admin/card-fleet-ops" element={
                            <Layout>
                                <CardFleetOpsRoute />
                            </Layout>
                        } />
                        <Route path="/employees" element={
                            <Layout>
                                <Employees />
                            </Layout>
                        } />
                        <Route path="/locations" element={
                            <Layout>
                                <Locations />
                            </Layout>
                        } />
                        <Route path="/neo-status" element={
                            <Layout>
                                <NeoSalesStatus />
                            </Layout>
                        } />
                        {/* THROWAWAY wireframes, admin only, sample data. Delete after approval. */}                        <Route path="/insights-wireframes" element={
                            <Layout>
                                <InsightsWireframes />
                            </Layout>
                        } />
                        {/* Super-admin control: the grocery add-on on a property. */}
                        <Route path="/grocery/property-addon" element={
                            <Layout>
                                <GroceryPropertyAddon />
                            </Layout>
                        } />
                        <Route path="/analytics" element={
                            <Layout>
                                <Analytics />
                            </Layout>
                        } />
                        
                        {/* ROI & Reports */}
                        <Route path="/roi" element={
                            <Layout>
                                <ROIDashboard />
                            </Layout>
                        } />
                        <Route path="/sales-report" element={
                            <Layout>
                                <SalesReport />
                            </Layout>
                        } />
                        <Route path="/sales-calculation-tool" element={
                            <Layout>
                                <SalesCalculationTool />
                            </Layout>
                        } />
                        <Route path="/shift-insights" element={
                            <Layout>
                                <ShiftInsights />
                            </Layout>
                        } />
                        <Route path="/location-health" element={
                            <Layout>
                                <LocationHealth />
                            </Layout>
                        } />
                        <Route path="/executive-summary" element={
                            <Layout>
                                <ExecutiveSummary />
                            </Layout>
                        } />
                        <Route path="/command-center" element={
                            <Layout>
                                <CommandCenter />
                            </Layout>
                        } />
                        <Route path="/manual" element={
                            <Layout>
                                <ManualList />
                            </Layout>
                        } />
                        <Route path="/manual/:sectionId" element={
                            <Layout>
                                <ManualSection />
                            </Layout>
                        } />
                        <Route path="/command-center/cameras" element={
                            <Layout>
                                <CamerasPage />
                            </Layout>
                        } />
                        <Route path="/brand-comparison" element={
                            <Layout>
                                <BrandComparison />
                            </Layout>
                        } />
                        
                        {/* Admin Routes */}
                        <Route path="/admin/brands" element={
                            <Layout>
                                <AdminBrands />
                            </Layout>
                        } />
                        <Route path="/admin/clients" element={
                            <Layout>
                                <AdminClients />
                            </Layout>
                        } />
                        <Route path="/admin/users" element={
                            <Layout>
                                <AdminUsers />
                            </Layout>
                        } />
                        <Route path="/admin/settings" element={
                            <Layout>
                                <AdminSettings />
                            </Layout>
                        } />
                        <Route path="/admin/ask-chef-voices" element={
                            <Layout>
                                <AskChefVoicesPage />
                            </Layout>
                        } />
                        <Route path="/admin/ask-chef-review" element={
                            <Layout>
                                <AskChefReviewPage />
                            </Layout>
                        } />
                        <Route path="/admin/diagnostics" element={
                            <Layout>
                                <AdminDiagnostics />
                            </Layout>
                        } />
                        <Route path="/admin/diagnostics/maraddo" element={
                            <Layout>
                                <MaraddoDiagnostic />
                            </Layout>
                        } />
                        <Route path="/admin/reconciliation" element={
                            <Layout>
                                <AdminReconciliation />
                            </Layout>
                        } />
                        <Route path="/admin/tap-health" element={
                            <Layout>
                                <TapHealthMonitor />
                            </Layout>
                        } />
                        {/* P_WELCOME_HUB_SCHEDULER_TAB_1.0 — standalone
                            /admin/scheduler retained as an alias that
                            redirects to the Communications hub tab so
                            any saved super-admin bookmarks keep working. */}
                        <Route
                            path="/admin/scheduler"
                            element={<Navigate to="/admin/user-welcome-dispatch?tab=scheduler" replace />}
                        />
                        <Route path="/admin/user-welcome-dispatch" element={
                            <Layout>
                                <UserWelcomeDispatch />
                            </Layout>
                        } />
                        <Route path="/my-settings" element={
                            <Layout>
                                <MySettings />
                            </Layout>
                        } />
                        <Route path="/settings/communication-preferences" element={
                            <Layout>
                                <CommunicationPreferences />
                            </Layout>
                        } />
                        <Route path="/organization/settings" element={
                            <Layout>
                                <OrganizationSettings />
                            </Layout>
                        } />
                        <Route path="/admin/platform-values" element={
                            <Layout>
                                <PlatformValuesSettings />
                            </Layout>
                        } />
                        <Route path="/admin/notifications" element={
                            <Layout>
                                <EmployeeNotifications />
                            </Layout>
                        } />
                        <Route path="/admin/audit-logs" element={
                            <Layout>
                                <AuditLogs />
                            </Layout>
                        } />
                        <Route path="/redemption-investigation" element={
                            <Layout>
                                <RedemptionInvestigation />
                            </Layout>
                        } />
                        <Route path="/admin/permissions" element={
                            <Layout>
                                <PermissionsManager />
                            </Layout>
                        } />
                        <Route path="/admin/insight-observation" element={
                            <Layout>
                                <AdminInsightObservation />
                            </Layout>
                        } />
                        <Route path="/admin/broadcasts" element={
                            <Layout>
                                <AdminBroadcasts />
                            </Layout>
                        } />
                        <Route path="/admin/recognition" element={
                            <Layout>
                                <AdminRecognition />
                            </Layout>
                        } />
                        <Route path="/admin/activity" element={
                            <Layout>
                                <AdminActivity />
                            </Layout>
                        } />
                        <Route path="/timeline" element={
                            <Layout>
                                <Timeline />
                            </Layout>
                        } />
                        <Route path="/weekly-report" element={
                            <Layout>
                                <WeeklyReportCard />
                            </Layout>
                        } />
                        <Route path="/management/audit" element={
                            <Layout>
                                <ManagementAudit />
                            </Layout>
                        } />
                        <Route path="/url-registry" element={
                            <Layout>
                                <URLRegistry />
                            </Layout>
                        } />
                        <Route path="/review-shield-results" element={
                            <Layout>
                                <ReviewShieldResults />
                            </Layout>
                        } />

                        <Route path="/patron-points" element={
                            <Layout>
                                <PatronCommand />
                            </Layout>
                        } />

                        <Route path="/voucher-reports" element={
                            <Layout>
                                <VoucherDashboard />
                            </Layout>
                        } />

                        <Route path="/voucher-settings" element={
                            <Layout>
                                <VoucherSettings />
                            </Layout>
                        } />

                        {/* Promo Engine — the portal page and the public promo screen. */}
                        <Route path="/promos" element={
                            <Layout>
                                <Promos />
                            </Layout>
                        } />

                        <Route path="/multi-location-links" element={
                            <Layout>
                                <MultiLocationLinks />
                            </Layout>
                        } />
                        <Route path="/multi-location-links/:id" element={
                            <Layout>
                                <MultiLocationLinkEditor />
                            </Layout>
                        } />

                        <Route path="/test-data-cleanup" element={
                            <Layout>
                                <TestDataCleanup />
                            </Layout>
                        } />

                        <Route path="/manager-access" element={
                            <Layout>
                                <ManagerAccessAdmin />
                            </Layout>
                        } />

                        {/* Hotel — August 2026 additions */}
                        <Route path="/hotel-results" element={
                            <Layout>
                                <HotelResults />
                            </Layout>
                        } />
                        <Route path="/hotel-setup" element={
                            <Layout>
                                <HotelSetup />
                            </Layout>
                        } />
                        <Route path="/hotel-rooms" element={
                            <Layout>
                                <HotelRooms />
                            </Layout>
                        } />
                        <Route path="/hotel-ops" element={
                            <Layout>
                                <HotelOpsPortal />
                            </Layout>
                        } />
                        <Route path="/card-guide" element={
                            <Layout>
                                <CardProgrammingGuide />
                            </Layout>
                        } />
                        <Route path="/hotel-manual" element={
                            <Layout>
                                <HotelManual />
                            </Layout>
                        } />

                        {/* Operational Base — Phase 2 shell (dark luxury, capability-driven) */}
                        <Route path="/operational-base" element={<OperationalBaseGate><OperationalBaseHome /></OperationalBaseGate>} />
                        <Route path="/operational-base/modules" element={<OperationalBaseGate><DemoRouteGuard><ModuleManagement /></DemoRouteGuard></OperationalBaseGate>} />
                        <Route path="/operational-base/activity" element={<OperationalBaseGate><DemoRouteGuard><ActivityPulsePage /></DemoRouteGuard></OperationalBaseGate>} />
                        <Route path="/operational-base/activity/security" element={<OperationalBaseGate><DemoRouteGuard><ActivityPulsePage /></DemoRouteGuard></OperationalBaseGate>} />
                        <Route path="/operational-base/templates" element={<OperationalBaseGate><DemoRouteGuard><TemplatesPage /></DemoRouteGuard></OperationalBaseGate>} />
                        <Route path="/operational-base/wizard" element={<OperationalBaseGate><DemoRouteGuard><SetupWizard /></DemoRouteGuard></OperationalBaseGate>} />
                        <Route path="/operational-base/analytics" element={<OperationalBaseGate><OperationalBaseAnalytics /></OperationalBaseGate>} />
                        <Route path="/operational-base/review-assistant" element={<OperationalBaseGate><ReviewAssistant /></OperationalBaseGate>} />
                        <Route path="/operational-base/review-shield" element={<OperationalBaseGate><ReviewShieldIntelligence /></OperationalBaseGate>} />

                        {/* Default Redirect */}
                        <Route path="/" element={<Navigate to="/login" replace />} />
                        <Route path="*" element={<Navigate to="/login" replace />} />
                    </Routes>
                                        </InsightProvider>
                                    </DemoModeProvider>
                                </DemoProvider>
                            </GuidanceProvider>
                            </InvestigationTourProvider>
                </TourProvider>
            </BrowserRouter>
                    </HelperDockProvider>
                </CapabilityProvider>
            </ThemeProvider>
        </AuthProvider>
        </NavLayoutProvider>
    );
}

export default App;
